import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { templateAPI } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { Image, Video, Globe, Lock, Crown, Eye, Copy, FileText, User, Calendar, Tag, Clock } from 'lucide-react'
import { formatOccasionDisplay, isOccasionActive } from '../../utils/occasionUtils'
import { toUtcDate } from '../../utils/dateFormat'

const RecentDesigns = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchMyTemplates = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await templateAPI.getMyTemplates()
      
      if (response.data.success) {
        setTemplates(response.data.data.templates || [])
      } else {
        setError(response.data.message || 'Failed to fetch your templates')
      }
    } catch (error) {
      console.error('Error fetching my templates:', error)
      setError('Failed to load your templates')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMyTemplates()
  }, [])

  const handleDesignClick = (template) => {
    navigate(`/editor/${template?.id}`)
  }

  const handleCreateNew = () => {
    navigate('/editor')
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown'
    try {
      return toUtcDate(dateString).toLocaleDateString('en-IN', { 
        timeZone: 'Asia/Kolkata',
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      })
    } catch {
      return 'Unknown'
    }
  }

  // Get template type
  const getTemplateType = (template) => {
    return template?.type === 'video' ? 'Video' : 'Image'
  }

  // Get template status
  const getTemplateStatus = (template) => {
    if (template?.is_public !== false) return 'Public'
    return 'Private'
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Recent designs</h2>
        </div>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Recent designs</h2>
        </div>
        <div className="text-center text-red-600">
          <p>{error}</p>
          <button 
            onClick={fetchMyTemplates}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Recent designs</h2>
        
        <div className="flex items-center space-x-3">
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Table Headers (List View) */}
      {viewMode === 'list' && (
        <div className="grid grid-cols-12 gap-4 px-4 py-2 text-sm font-medium text-gray-500 border-b border-gray-200">
          <div className="col-span-4">Name</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-1">Stage</div>
          <div className="col-span-1"></div>
        </div>
      )}

      {/* Templates Grid/List */}
      <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6' : 'space-y-2'}>
        {templates.map((template) => (
          <div key={template?.id}>
            {viewMode === 'grid' ? (
              /* Grid View */
              <div
                className="group cursor-pointer"
                onClick={() => handleDesignClick(template)}
              >
                <div className="relative overflow-hidden rounded-lg aspect-[4/5] mb-3">
                  {template?.thumbnailUrl ? (
                    <img
                      src={template?.thumbnailUrl}
                      alt={template?.name}
                      className="w-full h-full object-contain bg-gray-100"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                      <div className="text-center text-gray-500">
                        <Image className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-xs">No preview</p>
                      </div>
                    </div>
                  )}

                  {/* Hover Actions */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200">
                    <button className="px-4 py-2 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors">
                      Edit design
                    </button>
                  </div>

                  {/* Status badges */}
                  <div className="absolute top-2 right-2 flex flex-col gap-1">
                    {(template?.isPremium || template?.isPro) && (
                      <div className="bg-amber-500 text-white rounded-full p-1">
                        <Crown className="w-3 h-3" />
                      </div>
                    )}
                    {template?.is_public !== false ? (
                      <div className="bg-green-500 text-white rounded-full p-1">
                        <Globe className="w-3 h-3" />
                      </div>
                    ) : (
                      <div className="bg-gray-500 text-white rounded-full p-1">
                        <Lock className="w-3 h-3" />
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2">
                    {template?.name}
                  </h3>
                  <p className="text-xs text-gray-500">{formatDate(template?.createdAt)}</p>
                </div>
              </div>
            ) : (
              /* List View */
              <div
                className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                onClick={() => handleDesignClick(template)}
              >
                <div className="col-span-4 flex items-center space-x-3">
                  <div className="w-12 h-12 rounded bg-gray-100 flex-shrink-0 overflow-hidden">
                    {template?.thumbnailUrl ? (
                      <img
                        src={template?.thumbnailUrl}
                        alt={template?.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <Image className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                  <span className="font-medium text-gray-900 truncate">{template?.name}</span>
                </div>
                <div className="col-span-2 flex items-center text-sm text-gray-600">
                  {getTemplateType(template)}
                </div>
                <div className="col-span-2 flex items-center text-sm text-gray-600">
                  {getTemplateStatus(template)}
                </div>
                <div className="col-span-2 flex items-center text-sm text-gray-600">
                  {String(template?.Category?.name || template?.category || 'General')}
                </div>
                <div className="col-span-1 flex items-center text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    template?.stage === 'approved' ? 'bg-green-100 text-green-800' :
                    template?.stage === 'submitted' ? 'bg-yellow-100 text-yellow-800' :
                    template?.stage === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {template?.stage || 'pending'}
                  </span>
                </div>
                <div className="col-span-1 flex items-center justify-end">
                  <button className="p-1 hover:bg-gray-200 rounded">
                    <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Create New Design Card (Grid View Only) */}
        {viewMode === 'grid' && (
          <div
            className="group cursor-pointer"
            onClick={handleCreateNew}
          >
            <div className="relative overflow-hidden rounded-lg aspect-[4/5] mb-3 border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors">
              <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 group-hover:text-blue-500 transition-colors">
                <svg className="w-12 h-12 mb-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">Create new</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {templates.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No recent designs</h3>
          <p className="text-gray-500 mb-6">Start creating your first design</p>
          <button
            onClick={handleCreateNew}
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-lg hover:from-purple-600 hover:to-blue-700 transition-all duration-200 font-medium"
          >
            Create a design
          </button>
        </div>
      )}
    </div>
  )
}

export default RecentDesigns
